import React, {ReactNode} from 'react';
import {ActivityIndicator, LayoutChangeEvent, Pressable, View} from 'react-native';

import * as designTokens from '../../design-system/tokens';
import {FIELD_ERROR_COLOR} from '../../design-system/tokens/colour';
import SignUpPageLayout from '../../screens/account/signUp/SignUpPageLayout';
import theme from '../../style/theme';
import CountdownTimer from '../rewards/CountdownTimer';
import Button from '../themed/Button';
import Text from '../themed/Text';
import ValidationMessage from '../themed/ValidationMessage';
import BackChevron from '../ui-controls/BackChevron';

/**
 * Length of the resend cooldown the countdown ring is drawn against. Only the
 * Lite verification screen runs a cooldown; it should import this constant
 * rather than restate the number.
 */
export const RESEND_COOLDOWN_SECONDS = 30;

// The error line nests its own Text inside ValidationMessage because
// ValidationMessage does not forward testID or accessibility props. The colour
// is the shared form-error red (DIS-1853).
export const ERROR_TEXT_COLOUR = FIELD_ERROR_COLOR;

type Props = {
  /** Heading, for example `VERIFY YOUR MOBILE`. */
  title: string;

  /** Line under the heading naming where the code went. */
  intro: ReactNode;

  /** Resend confirmation line under the intro. Hidden when empty. */
  notice?: string;

  /** The owner's own `Controller` + `Otp`. */
  otp: ReactNode;

  /**
   * Wrong-code message. Wide: an empty value keeps the line's height. Narrow:
   * the line takes no height while empty (292:9049).
   */
  error?: string;

  verifyDisabled: boolean;
  verifying: boolean;
  onVerify: () => void;

  /**
   * `'ready'` shows the resend button, `'sending'` shows the in-flight
   * indicator, and a number shows the cooldown seconds left.
   */
  resend: 'ready' | 'sending' | number;
  resendError?: string;
  onResend: () => void;

  /** Omit to hide the "Change number" link (the email branch has no route for it). */
  onChangeNumber?: () => void;
  onBack: () => void;

  /** Rendered between the intro and the OTP input. */
  children?: ReactNode;

  /** Rendered after "Change number". */
  footer?: ReactNode;

  /**
   * Opt-in desktop card (Figma D2 Welcome Back 536:4455). When set, the view
   * renders SignUpPageLayout's centred arrangement and reports its width here;
   * the owner sets it only while its own layout is wide. When absent the view
   * renders the bare column, exactly as before.
   */
  centredOnLayout?: (event: LayoutChangeEvent) => void;
};

const paragraph = designTokens.type.paragraph.p2;
// Button/Secondary in Figma (95:3644) on every width.
const link = [designTokens.type.button.secondary, {color: theme.colour.text.link.primary}];

// Narrow column (95:3644, 95:3643, 292:9049): every gap from layout.mobile.
const narrowRhythm = designTokens.layout.mobile;
const narrowBody = [
  designTokens.type.paragraph.p1,
  designTokens.lineHeight.paragraph.p1,
  {textAlign: 'center' as const},
];
// Invalid code: P2 in Universal/Discount Red, 8 below the boxes. Takes no
// height while empty, so VERIFY moves down with the message, as 292:9049 draws.
const narrowErrorStyle = [
  designTokens.type.paragraph.p2,
  designTokens.lineHeight.paragraph.p2,
  {color: designTokens.universal.discountRed},
];

// Desktop card only (centredOnLayout). Same tokens as the Welcome card
// (RewardsSignInScreen): H1 and P1 with their line heights, centred; every gap
// from layout.desktop. The narrow column keeps its own values below.
const wideRhythm = designTokens.layout.desktop;
const wideHeading = [
  designTokens.type.heading.h1,
  designTokens.lineHeight.heading.h1,
  {color: theme.colour.text.heading.primary, textAlign: 'center' as const},
];
// P2, as the resend error line below. Applied to the outer line (spacing) and
// to the inner themed Text (size, colour).
const wideErrorStyle = [
  designTokens.type.paragraph.p2,
  {marginTop: wideRhythm.stackRelated, marginBottom: 0, textAlign: 'center' as const},
];
const wideErrorTextStyle = [
  designTokens.type.paragraph.p2,
  {color: ERROR_TEXT_COLOUR, textAlign: 'center' as const},
];
const wideBody = [
  designTokens.type.paragraph.p1,
  designTokens.lineHeight.paragraph.p1,
  {textAlign: 'center' as const},
];

const ResendControl = ({
  resend,
  onResend,
}: {
  resend: Props['resend'];
  onResend: Props['onResend'];
}) => {
  if (resend === 'sending') {
    return (
      <View
        style={{flexDirection: 'row', alignItems: 'center', gap: designTokens.space.s6}}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
        testID="resend-sending-indicator">
        <ActivityIndicator size="small" />
        <Text style={paragraph}>Sending…</Text>
      </View>
    );
  }

  if (typeof resend === 'number') {
    return (
      <CountdownTimer
        seconds={resend}
        total={RESEND_COOLDOWN_SECONDS}
        testID="resend-countdown-timer"
      />
    );
  }

  return (
    <Text
      onPress={onResend}
      style={link}
      accessibilityRole="button"
      accessibilityLabel="Resend verification code"
      testID="resend-code-button">
      Resend code
    </Text>
  );
};

/**
 * The phone-verification column shared by account creation and Lite sign-up.
 * Presentational only: the owner keeps its scroll container, form, API calls
 * and timers, and passes its OTP input in through `otp`.
 */
const PhoneVerificationView = ({
  title,
  intro,
  notice,
  otp,
  error,
  verifyDisabled,
  verifying,
  onVerify,
  resend,
  resendError,
  onResend,
  onChangeNumber,
  onBack,
  children,
  footer,
  centredOnLayout,
}: Props) => {
  /* Left-anchored as in the sign-up password step (and board 6.png): a bare
     Pressable stretches to the column width and centres its icon. */
  const back = (
    <View style={{alignItems: 'flex-start'}}>
      <BackChevron onPress={onBack} />
    </View>
  );

  const wide = Boolean(centredOnLayout);

  // The wrong-code line. Wide: inside ValidationMessage, as #515 left it.
  const errorLine = wide ? (
    <ValidationMessage style={wideErrorStyle}>
      {/* P2 in the error colour, because the themed Text sets its own size
          and would ignore the outer one. */}
      <Text
        style={wideErrorTextStyle}
        accessibilityRole={error ? 'alert' : undefined}
        accessibilityLiveRegion="polite"
        testID="verification-error">
        {error || ' '}
      </Text>
    </ValidationMessage>
  ) : (
    <Text
      style={[narrowErrorStyle, error ? {marginTop: designTokens.space.s8} : null]}
      accessibilityRole={error ? 'alert' : undefined}
      accessibilityLiveRegion="polite"
      testID="verification-error">
      {error || ''}
    </Text>
  );

  const resendRow =
    resend !== 'ready' ? (
      // The spinner and the cooldown ring are Views, so they cannot sit inside Text.
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: designTokens.space.s4,
          marginTop: wide ? wideRhythm.stackRelated : designTokens.space.s40,
        }}>
        <Text style={paragraph}>Having trouble?</Text>
        <ResendControl resend={resend} onResend={onResend} />
      </View>
    ) : (
      // One inline sentence, as account creation rendered it before extraction.
      <Text
        style={[
          paragraph,
          {
            marginTop: wide ? wideRhythm.stackRelated : designTokens.space.s40,
            textAlign: 'center',
          },
        ]}>
        {'Having trouble?'} <ResendControl resend={resend} onResend={onResend} />
      </Text>
    );

  const resendErrorLine = resendError ? (
    <Text
      style={[
        paragraph,
        {color: ERROR_TEXT_COLOUR, marginTop: designTokens.space.s4, textAlign: 'center'},
      ]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
      testID="resend-error">
      {resendError}
    </Text>
  ) : null;

  const verifyButton = (
    <View style={{marginTop: wide ? wideRhythm.stackGroup : narrowRhythm.stackGroup}}>
      <Button
        title={'VERIFY'}
        titleStyle={designTokens.type.button.primary}
        buttonStyle={
          wide
            ? {
                width: '100%',
                height: designTokens.control.ctaHeight,
                borderRadius: designTokens.radius.control,
              }
            : {width: '100%'}
        }
        onPress={onVerify}
        disabled={verifyDisabled}
        loading={verifying}
      />
    </View>
  );

  // Wide: the link spans the form column like Figma's 343 CTA row.
  const changeNumber = onChangeNumber ? (
    <View
      style={
        wide
          ? {marginTop: wideRhythm.stackGroup}
          : {alignItems: 'center', marginTop: narrowRhythm.stackGroup}
      }>
      <Pressable onPress={onChangeNumber} accessibilityRole="button" testID="change-number-button">
        <Text style={wide ? [link, {textAlign: 'center'}] : link}>Change number</Text>
      </Pressable>
    </View>
  ) : null;

  // Wide (536:4455): intro -> OTP is the card's section gap, so the OTP row
  // takes no top margin; OTP -> error -> resend are related gaps; resend ->
  // VERIFY and VERIFY -> Change number are group gaps (Figma 40 / 40).
  // Narrow (95:3644): OTP -> VERIFY 24, VERIFY -> resend 40, resend -> Change
  // number 24. Figma draws VERIFY above the resend row on mobile only.
  const controls = wide ? (
    <>
      {children}
      <View>{otp}</View>
      {errorLine}
      {resendRow}
      {resendErrorLine}
      {verifyButton}
      {changeNumber}
      {footer}
    </>
  ) : (
    <>
      {children}
      <View style={{marginTop: narrowRhythm.stackSection}}>{otp}</View>
      {errorLine}
      {verifyButton}
      {resendRow}
      {resendErrorLine}
      {changeNumber}
      {footer}
      <View style={{height: narrowRhythm.pageEnd}} />
    </>
  );

  if (centredOnLayout) {
    // Back arrow outside the card; heading and intro in the card's text block
    // (P1, as the Welcome card and 536:4455's 38px two-line body); the OTP
    // group, VERIFY and links in the 343 form column. No banner.
    return (
      <SignUpPageLayout
        centred
        isWide
        onLayout={centredOnLayout}
        testID="phoneVerification"
        control={back}
        heading={<Text style={wideHeading}>{title}</Text>}
        intro={
          <>
            <Text style={wideBody}>{intro}</Text>
            {notice ? (
              <Text style={[wideBody, {marginTop: wideRhythm.stackRelated}]}>{notice}</Text>
            ) : null}
          </>
        }
        main={controls}
      />
    );
  }

  return (
    <>
      {back}

      <Text
        style={[
          designTokens.type.heading.h1,
          designTokens.lineHeight.heading.h1,
          {
            color: theme.colour.text.heading.primary,
            marginTop: narrowRhythm.stackRelated,
            textAlign: 'center',
          },
        ]}>
        {title}
      </Text>

      <Text style={[narrowBody, {marginTop: narrowRhythm.stackRelated}]}>{intro}</Text>

      {notice ? (
        <Text style={[narrowBody, {marginTop: narrowRhythm.stackRelated}]}>{notice}</Text>
      ) : null}

      {controls}
    </>
  );
};

export default PhoneVerificationView;
